import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Loader2, Plus, CheckCircle, AlertTriangle, Clock, X, Shield, FileText } from "lucide-react";
import { Card, GoldButton } from "../shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  standard: "bg-blue-100 text-blue-700", critical: "bg-red-100 text-red-700",
  prior_to_docs: "bg-amber-100 text-amber-700", prior_to_funding: "bg-purple-100 text-purple-700",
};
const STATUS_STYLES = {
  open: "bg-amber-100 text-amber-700", in_progress: "bg-blue-100 text-blue-700",
  cleared: "bg-emerald-100 text-emerald-700", waived: "bg-gray-100 text-gray-600", failed: "bg-red-100 text-red-700",
};

export function ConditionsPanel({ dealId }) {
  const [conditions, setConditions] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/conditions/summary/${dealId}`, { headers });
      setConditions(res.data.conditions || []);
      setSummary(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/conditions/${id}`, { status }, { headers });
      fetch();
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-4" data-testid="conditions-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs">
          <span className="px-2 py-1 rounded-lg bg-amber-100 text-amber-700 font-medium">{summary.open || 0} open</span>
          <span className="px-2 py-1 rounded-lg bg-blue-100 text-blue-700 font-medium">{summary.in_progress || 0} in progress</span>
          <span className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-medium">{summary.cleared || 0} cleared</span>
          {(summary.critical || 0) > 0 && <span className="px-2 py-1 rounded-lg bg-red-100 text-red-700 font-bold">{summary.critical} critical</span>}
        </div>
        <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-700 text-white hover:bg-emerald-600" data-testid="add-condition-btn">
          <Plus className="h-3 w-3 inline mr-1" /> Add
        </button>
      </div>

      {conditions.length === 0 ? (
        <div className="text-center py-6 text-sm text-[#999]">No conditions on this deal</div>
      ) : (
        <div className="space-y-2">
          {conditions.map(c => (
            <div key={c.condition_id} className="flex items-start gap-3 p-3 rounded-xl border border-cream-warm bg-cream/50" data-testid={`condition-${c.condition_id}`}>
              <div className="mt-0.5">
                {c.status === "cleared" ? <CheckCircle className="h-4 w-4 text-emerald-500" /> :
                 c.severity === "critical" ? <AlertTriangle className="h-4 w-4 text-red-500" /> :
                 <Clock className="h-4 w-4 text-amber-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#333]">{c.description}</p>
                <div className="flex items-center gap-2 mt-1 text-[10px]">
                  <span className={`px-1.5 py-0 rounded font-bold ${SEVERITY_STYLES[c.severity] || ""}`}>{c.severity}</span>
                  <span className={`px-1.5 py-0 rounded font-bold ${STATUS_STYLES[c.status] || ""}`}>{c.status}</span>
                  <span className="text-[#aaa]">{c.source_type}</span>
                  {c.due_date && <span className="text-[#888]">Due: {new Date(c.due_date).toLocaleDateString()}</span>}
                </div>
              </div>
              {c.status !== "cleared" && c.status !== "waived" && (
                <button onClick={() => updateStatus(c.condition_id, "cleared")}
                  className="px-2 py-1 rounded-lg text-[10px] font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 shrink-0">Clear</button>
              )}
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showAdd && <AddConditionModal dealId={dealId} headers={headers} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); fetch(); }} />}
      </AnimatePresence>
    </div>
  );
}

function AddConditionModal({ dealId, headers, onClose, onCreated }) {
  const [form, setForm] = useState({ description: "", severity: "standard", source_type: "underwriter", notes: "", due_date: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.description.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/conditions`, { ...form, deal_id: dealId, due_date: form.due_date || null }, { headers });
      onCreated();
    } catch {}
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="add-condition-modal">
        <div className="px-4 sm:px-6 py-4 border-b border-cream-warm"><h3 className="text-base font-bold">Add Condition</h3></div>
        <div className="px-4 sm:px-6 py-5 space-y-3">
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Condition description..."
            className="w-full px-3 py-2 rounded-xl border border-cream-warm text-sm resize-none h-20 focus:border-emerald-400 outline-none" data-testid="condition-desc" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.severity} onChange={e => setForm(p => ({ ...p, severity: e.target.value }))} className="px-3 py-2 rounded-xl border border-cream-warm text-sm bg-white" data-testid="condition-severity">
              <option value="standard">Standard</option>
              <option value="critical">Critical</option>
              <option value="prior_to_docs">Prior to Docs</option>
              <option value="prior_to_funding">Prior to Funding</option>
            </select>
            <select value={form.source_type} onChange={e => setForm(p => ({ ...p, source_type: e.target.value }))} className="px-3 py-2 rounded-xl border border-cream-warm text-sm bg-white" data-testid="condition-source">
              <option value="underwriter">Underwriter</option>
              <option value="lender">Lender</option>
              <option value="investor">Investor</option>
              <option value="closing">Closing</option>
            </select>
          </div>
          <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 outline-none" data-testid="condition-due" />
        </div>
        <div className="px-4 sm:px-6 py-4 border-t border-cream-warm flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-cream-warm text-sm">Cancel</button>
          <button onClick={submit} disabled={submitting || !form.description.trim()} data-testid="condition-submit"
            className="px-5 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Condition"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ApprovalsPanel({ dealId }) {
  const [records, setRecords] = useState([]);
  const [level, setLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApprove, setShowApprove] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/approvals?deal_id=${dealId}`, { headers }),
      axios.get(`${API}/approvals/level/${dealId}`, { headers }),
    ]).then(([recs, lvl]) => {
      setRecords(recs.data.records || []);
      setLevel(lvl.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [dealId]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-4" data-testid="approvals-panel">
      {/* Approval Level Info */}
      {level && (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-cream-warm bg-cream">
          <Shield className="h-5 w-5 text-purple-600" />
          <div>
            <div className="text-sm font-semibold text-[#0A0A0A]">Level {level.approval_level} Approval Required</div>
            <div className="text-[10px] text-[#888]">
              Loan: ${(level.loan_amount || 0).toLocaleString()} &bull; Required: {(level.required_roles || []).join(", ")}
              {level.has_exception && <span className="text-red-600 ml-2 font-bold">EXCEPTION</span>}
            </div>
          </div>
          <button onClick={() => setShowApprove(true)} className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-700 text-white hover:bg-emerald-600" data-testid="record-approval-btn">
            Record Approval
          </button>
        </div>
      )}

      {/* Approval Records */}
      {records.length === 0 ? (
        <div className="text-center py-6 text-sm text-[#999]">No approval records yet</div>
      ) : (
        <div className="space-y-2">
          {records.map(r => (
            <div key={r.approval_id} className="p-3 rounded-xl border border-cream-warm" data-testid={`approval-${r.approval_id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${r.decision === "approved" || r.decision === "conditionally_approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.decision.replace("_", " ").toUpperCase()}</span>
                  <span className="text-xs text-[#555]">Level {r.approval_level} &bull; {r.approval_type}</span>
                </div>
                <span className="text-[10px] text-[#aaa]">{r.approver_name || r.approver_role} &bull; {new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.notes && <p className="text-xs text-[#666] mt-1">{r.notes}</p>}
              {r.exception_rationale && (
                <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                  <strong>Exception:</strong> {r.exception_category} — {r.exception_rationale}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {showApprove && <RecordApprovalModal dealId={dealId} headers={headers} onClose={() => setShowApprove(false)} onCreated={() => { setShowApprove(false); window.location.reload(); }} />}
      </AnimatePresence>
    </div>
  );
}

function RecordApprovalModal({ dealId, headers, onClose, onCreated }) {
  const [form, setForm] = useState({ decision: "approved", approval_type: "standard", notes: "", exception_category: "", exception_rationale: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/approvals`, { ...form, deal_id: dealId }, { headers });
      onCreated();
    } catch {}
    finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="record-approval-modal">
        <div className="px-4 sm:px-6 py-4 border-b border-cream-warm"><h3 className="text-base font-bold">Record Approval</h3></div>
        <div className="px-4 sm:px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select value={form.decision} onChange={e => setForm(p => ({ ...p, decision: e.target.value }))} className="px-3 py-2.5 rounded-xl border border-cream-warm text-sm bg-white" data-testid="approval-decision">
              <option value="approved">Approved</option>
              <option value="conditionally_approved">Conditionally Approved</option>
              <option value="declined">Declined</option>
            </select>
            <select value={form.approval_type} onChange={e => setForm(p => ({ ...p, approval_type: e.target.value }))} className="px-3 py-2.5 rounded-xl border border-cream-warm text-sm bg-white" data-testid="approval-type">
              <option value="standard">Standard</option>
              <option value="conditional">Conditional</option>
              <option value="exception">Exception</option>
            </select>
          </div>
          <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Approval notes..."
            className="w-full px-3 py-2 rounded-xl border border-cream-warm text-sm resize-none h-16 focus:border-emerald-400 outline-none" data-testid="approval-notes" />
          {form.approval_type === "exception" && (
            <>
              <input value={form.exception_category} onChange={e => setForm(p => ({ ...p, exception_category: e.target.value }))} placeholder="Exception category"
                className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 outline-none" data-testid="exception-category" />
              <textarea value={form.exception_rationale} onChange={e => setForm(p => ({ ...p, exception_rationale: e.target.value }))} placeholder="Rationale and compensating strengths..."
                className="w-full px-3 py-2 rounded-xl border border-cream-warm text-sm resize-none h-16 focus:border-emerald-400 outline-none" data-testid="exception-rationale" />
            </>
          )}
        </div>
        <div className="px-4 sm:px-6 py-4 border-t border-cream-warm flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-cream-warm text-sm">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="approval-submit"
            className="px-5 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
